/* eslint-disable max-lines */
import { Injectable, inject } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { combineLatest, map, Observable, startWith, take } from 'rxjs';
import { DEFAULT_CURRENCY } from '../constants/default.const';
import { ComponentsToLoad, SubComponentsToLoad } from '../enums/components-to-load.enum';
import { DialogContentType } from '../enums/dialog-type.enum';
import { GapiLicense } from '../enums/gapi-license';
import { PaymentTransactionType } from '../enums/payment-method.enum';
import { ConsumerPortalConfig } from '../models';
import { Customer } from '../models/consumer';
import { ContractCP, ContractDetail, GetDocumentParam, LineItems } from '../models/contract-model';
import { FetchContractsParams } from '../models/contracts.model';
import { EventLogsRequest } from '../models/event-logs.interface';
import { FetchItemsOutParams } from '../models/itemsout.model';
import { LoginSession } from '../models/login-session.model';
import { PayMinimumAmount } from '../models/pay-minimum-amount.model';
import { ProductDetail } from '../models/product-detail';
import { PaymentMinMaxBoundary } from '../payment/models/payment-data.model';
import { PaymentAppPayOutput, PayOutputEvent, PorPayEvent, TransactionData, TransactionStatus } from '../payment/models/paymentOutput';
import { Processors } from '../payment/models/processors.interface';
import {
    loadVersionRedirection,
    loadVersionRedirectionFailure,
    setActiveTab,
    setAdminLoading,
    setAdminRoles,
    setAuthenticated,
    setDeviceType,
    setIsAdmin,
    setLoading,
    setRedirected,
    setSubActiveTab,
    setVersionRedirection
} from '../store/app-wide/app/app.actions';
import {
    selectActiveTab,
    selectAdminRoles,
    selectConsumerPortalState,
    selectIsAdmin,
    selectIsAuthenticated,
    selectIsLoading,
    selectIsMobile,
    selectIsRedirected,
    selectLoaded,
    selectShouldShowMenu,
    selectSubActiveTab,
    selectVersionRedirection
} from '../store/app-wide/app/app.selectors';
/* eslint-disable @typescript-eslint/naming-convention  */
import * as ConfigActions from '../store/app-wide/config/config.actions';
import * as ConfigSelectors from '../store/app-wide/config/config.selectors';
import { selectContractCurrency, selectCustomerCurrency } from '../store/app-wide/currencycode/currencycode.selectors';
import { setDialogDisplayed, setManageUserDialogDisplayed, showDialog } from '../store/app-wide/dialog/dialog.actions';
import { selectDialogContentType, selectIsDialogDisplayed, selectManageUserDialogDisplayed } from '../store/app-wide/dialog/dialog.selectors';
import { saveEventLogsData } from '../store/app-wide/event-logs/event-logs.actions';
import { changePageSizeValue } from '../store/app-wide/pagination/pagination.actions';
import { selectPageSizeValues } from '../store/app-wide/pagination/pagination.selectors';
import { processRoute, startPublicRedirection, startRedirection } from '../store/app-wide/redirection/redirection.actions';
import { selectIsConfigSet, selectRedirection, selectRedirectionSessionData } from '../store/app-wide/redirection/redirection.selectors';
import {
    clearContractDetailInStore,
    closePayment,
    continueMultipayment,
    convertQuoteToReservation,
    fetchContractDetail,
    fetchContractDetailLineItems,
    fetchContractDetailPayment,
    fetchContracts,
    loadContractAction,
    openContract,
    resetContractsLatestToShow,
    resetPageNumberContract,
    setContractDetailData,
    setPaymentOpen,
    setQuoteConversionInProgress,
    setSelectedContracts,
    setTrackedItems,
    unsetSelectedContracts
} from '../store/domains/contract/contract.actions';
import {
    getContracts,
    getContractsTotal,
    getConvertedContractIds,
    getCreditContractsTotal,
    getPaymentOpen,
    getQuoteConversionInProgress,
    getSelectedContracts,
    getSelectedContractsModels,
    getSelectedContractsTotal,
    getSelectedCreditContracts,
    getSelectedNonCreditContracts,
    isContractSelectable,
    isContractSelected,
    isMaxContractSelected,
    selectContractDetail,
    selectContractError,
    selectContractHasPaymentHistory,
    selectContractLineItems,
    selectTotalPaidByContractId,
    getTrackedItems
} from '../store/domains/contract/contract.selectors';
import { CustomerSummaryActionTypes, loadCustomer } from '../store/domains/customer-summary/customer-summary.actions';
import { getCustomer, getCustomerContacts, getLivelinkExpiry, getLivelinkUrl, isCustomerContactsLoading, isLiveLinkLoading } from '../store/domains/customer/customer.selectors';
import { cancelLoadDocuments, downloadDocument, emptyDocumentData, loadDocument, loadEsignAndOpen, loadLinkAndOpen, openOrDownLoadDocument } from '../store/domains/document/document.actions';
import { DocumentData } from '../store/domains/document/document.reducers';
import {
    selectAreDocumentsExist,
    selectAreDocumentsExistAfterLoading,
    selectDocumentDataById,
    selectDocumentDataByType,
    selectIsDocumentExists,
    selectIsDocumentLoading
} from '../store/domains/document/document.selectors';
import { fetchItemsOut } from '../store/domains/items-out-actions/items-out-action.actions';
import { isLineItemCalledOff } from '../store/domains/itemsout/itemsout.selectors';
import {
    createTransaction,
    loadPaymentProcessorAvailability,
    onPaymentFinished,
    setDefaultPaymentMid,
    setDialogEnabled,
    setPaymentSectionEnabled,
    setPaymentSubject,
    setTransactionLoaded,
    successVerifyPayment
} from '../store/domains/payment/payment.actions';
import * as PaymentTokenActions from '../store/payment-token/payment-token.actions';
import {
    selectCrossAppEvent,
    selectDefaultPaymentMid,
    selectDialogEnabled,
    selectErrorString,
    selectIsPaymentApproved,
    selectLastPaymentConfirmationPending,
    selectIsPaymentProcessorAvailable,
    selectLastPaymentDetail,
    selectLastPaymentDetailTransactionData,
    selectLastPaymentDetailTransactionId,
    selectLastPaymentDetailTransactionStatus,
    selectMultipaymentBoundaries,
    selectPaymentAppInput,
    selectPaymentSectionEnabled,
    selectTransactionCurrencyCode,
    selectTransactionLoaded
} from '../store/domains/payment/payment.selectors';
import { loadProduct, loadProductSuccess, loadPublicProductByAssetTag, resetProduct } from '../store/domains/product/product.actions';
import { ProductState } from '../store/domains/product/product.reducers';
import { selectProduct, selectProductError, selectProductLoading } from '../store/domains/product/product.selectors';
import { AppState } from '../store/app-wide/app/app.state';
import { ConfigState } from '../store/app-wide/config/config.state';
import { PaymentState } from '../store/domains/payment/payment.state';
import { Redirection } from '../types/redirection.type';
import { ConsumerPortalState } from '../types/state.type';
import { FeatureToggleService } from './feature-toggle.service';
import { PathRoutingService } from './path-routing.service';
import { PdfService } from './pdf.service';
import { VersionToggleService } from './version-toggle';
import { clearInvoiceDetailInStore, continueInvoiceMultipayment, fetchInvoiceDetail, setSelectedInvoices, unsetSelectedInvoices } from '../store/domains/invoices/invoices.actions';
import {
    getInvoicesTotal,
    getSelectedInvoices,
    getSelectedInvoicesModels,
    isInvoiceSelectable,
    isInvoiceSelected,
    isMaxInvoiceSelected,
    selectInvoiceDetail
} from '../store/domains/invoices/invoices.selectors';
import { Invoice } from '../models/invoice-model';
import { Depot } from '../shared/models/depot.interface';
import { assignOrRevokePermission, cleanAccountContact, loadCustomerContacts, loadLiveLink, setContactActiveStatus } from '../store/domains/customer/customer.actions';
import { selectOrganizationDepots, selectOrganizationExternalCredentials } from '../store/domains/organization/organization.selectors';
import { loadDepots, loadExternalCredentials } from '../store/domains/organization/organization.actions';
import { ContactResponse } from '../admin/interfaces/find-email.interface';
import { ProductDocumentType } from '../enums/product-document-type.enum';
import { loadAccountFeature } from '../store/domains/feature-account/account.actions';
import { AssignUserPermissionRequest } from '../admin/interfaces/user-accout.interface';
import { selectAccountFeaturesLoaded } from '../store/domains/feature-account/account.selectors';
import { ExternalCredentials } from '@por/shared/core';
import { AdminRole } from '@por/consumer-login/shared-lib';
import { TrackingResponse } from '../models/tracking-result.model';
/**
 * This service is used to manage Consumer Portal Config to store
 */
@Injectable({
    providedIn: 'root'
})
export class AppFacadeService {
    private readonly store = inject<Store<AppState>>(Store);
    private readonly paymentStore = inject<Store<PaymentState>>(Store);
    private readonly configStore = inject<Store<ConfigState>>(Store);
    readonly pdf = inject(PdfService);
    readonly versionToggleService = inject(VersionToggleService);
    readonly featureToggleService = inject(FeatureToggleService);

    // AdminRole values mirror the admin entries of ComponentsToLoad, so a role maps 1:1 to the tab it unlocks.
    private allowedAdminTabs: AdminRole[] = [];

    setConfig(config: ConsumerPortalConfig): void {
        this.store.dispatch(ConfigActions.setConfig({ config }));
    }

    getConfig(): Observable<ConsumerPortalConfig> {
        return this.configStore.pipe(select(ConfigSelectors.selectConfig));
    }

    getConfigPayMinimumAmount(): Observable<PayMinimumAmount> {
        return this.store.pipe(select(ConfigSelectors.selectPayMinimumAmount));
    }

    setLoading(isLoading: boolean): void {
        this.store.dispatch(setLoading(isLoading));
    }

    getLoader(): Observable<boolean> {
        return this.store.pipe(select(selectIsLoading));
    }

    getPdfdocument(docType: string): Observable<DocumentData[]> {
        return this.store.pipe(select(selectDocumentDataByType(docType)));
    }

    isDocumentExists(docType: string): Observable<boolean> {
        return this.store.pipe(select(selectIsDocumentExists(docType)));
    }

    isDocumentLoading(docType: string): Observable<boolean> {
        return this.store.pipe(select(selectIsDocumentLoading(docType)));
    }

    checkIfDocumentsExistAfterLoading(): Observable<boolean> {
        return this.store.pipe(select(selectAreDocumentsExistAfterLoading));
    }

    checkIfDocumentsExist(): Observable<boolean> {
        return this.store.pipe(select(selectAreDocumentsExist));
    }

    emptyDocument() {
        this.store.dispatch(emptyDocumentData());
    }

    loadDocuments(contractdetails: GetDocumentParam) {
        this.store.dispatch(loadDocument({ contractdetails }));
    }

    openPdfdocument(docType: string): Observable<DocumentData[]> {
        return this.store.pipe(take(1), select(selectDocumentDataByType(docType)));
    }

    cancelLoadDocuments() {
        this.store.dispatch(cancelLoadDocuments());
    }

    /**
     * Dispatch action to save event logs
     * @param data
     */
    saveEventLogs(data: EventLogsRequest) {
        this.store.dispatch(saveEventLogsData({ payload: data }));
    }

    /**
     * Set active tab to state
     * @param activeTab
     * @param cleatSelectedContractId
     */
    setActiveTab(activeTab: ComponentsToLoad, clearSelectedContractId = false): void {
        if (activeTab === ComponentsToLoad.Logout) {
            this.updateLocation(activeTab);
            return;
        }
        // Block navigation to pages the user's Role doesn't grant (no roles → unrestricted).
        if (!this.isAdminTabAllowed(activeTab)) {
            return;
        }
        if (!this.isTabLicenseAllowed(activeTab)) {
            return;
        }
        this.store.dispatch(setActiveTab({ activeTab, clearSelectedContractId }));
    }

    /**
     * Store the admin pages (tabs) the user may access. Empty → no restriction.
     */
    setAdminRoles(adminRoles?: AdminRole[]): void {
        this.allowedAdminTabs = adminRoles?.length ? adminRoles : [];
        this.store.dispatch(setAdminRoles({ adminRoles: this.allowedAdminTabs }));
    }

    /**
     * Apply the user's admin page-access Role to the active tab. If the user is restricted
     * to a single page, that page is set as the active tab. If the user is restricted to
     * multiple pages, the first page in their Role is set as the active tab.
     * If the user has no Role, no change is made.
     */
    applyAdminRoles(roles?: AdminRole[]): void {
        if (!roles || roles.length === 0) {
            return;
        }

        const loadTab = roles[0] as unknown as ComponentsToLoad;
        if (!loadTab) {
            return;
        }

        let activeTab: ComponentsToLoad | undefined;
        this.store.pipe(select(selectActiveTab), take(1)).subscribe(tab => (activeTab = tab));
        if (roles.includes(activeTab as unknown as AdminRole)) {
            return;
        }

        this.setActiveTab(loadTab);
    }

    getAdminRoles(): Observable<AdminRole[]> {
        return this.store.pipe(select(selectAdminRoles));
    }

    /**
     * Whether the admin menu should be displayed. Hidden only when the user is
     * restricted to exactly one page.
     */
    shouldShowMenu(): Observable<boolean> {
        return this.store.pipe(select(selectShouldShowMenu));
    }

    /**
     * Synchronous check used to gate tab navigation. Unrestricted when no roles are set.
     */
    isAdminTabAllowed(tab: ComponentsToLoad): boolean {
        return this.allowedAdminTabs.length === 0 || this.allowedAdminTabs.includes(tab as unknown as AdminRole);
    }

    /** License/permission gate for tabs that require a license (Tracking). Unrestricted otherwise. */
    isTabLicenseAllowed(tab: ComponentsToLoad): boolean {
        if (tab === ComponentsToLoad.Tracking) {
            return (
                this.featureToggleService.isAvailable('canSeeTrackingTab') &&
                this.featureToggleService.isAvailable('trackingTab') &&
                this.versionToggleService.hasLicense(GapiLicense.ConsumerPortalTracking)
            );
        }
        return true;
    }

    /**
     * Get the active tab
     * @returns Observable<ComponentsToLoad>
     */
    getActiveTab(): Observable<ComponentsToLoad> {
        return this.store.pipe(select(selectActiveTab));
    }

    /**
     * Set sub active tab to state
     * @param subActiveTab
     */
    setSubActiveTab(subActiveTab: SubComponentsToLoad): void {
        this.store.dispatch(setSubActiveTab({ subActiveTab }));
    }

    /**
     * Get the sub active tab
     * @returns Observable<ComponentsToLoad>
     */
    getSubActiveTab(): Observable<SubComponentsToLoad | null> {
        return this.store.pipe(select(selectSubActiveTab));
    }

    /**
     * Update location history on address bar
     * @param tab
     * @returns
     */
    updateLocation(tab: ComponentsToLoad | string, id?: string): void {
        const currentState = history.state || window.location.href;
        if (this.versionToggleService.isConsumerQuickLinkVersion()) {
            return;
        }
        if (tab === ComponentsToLoad.Logout) {
            /**
             * Set to default view when logout performed
             */
            this.setActiveTab(ComponentsToLoad.AccountSummary);
            // Logout intentionally rewrites to '#', dropping ?token= so the session flips out of
            // QuickLink mode for return-to-login (auth-ui.service.returnToLogin). This MUST run
            // before the QuickLink guard below, or a contract/stock QL can never reach the login view.
            history.replaceState(currentState, '', `#`);
            return;
        }
        // A QuickLink session's identity lives in the ?token= query and its storage is keyed on
        // QuickLinkService.isQuickLink (href.includes('token=')). Any non-logout hash write resolves
        // against <base href> and drops the query, detaching the sessionStorage session → 401s
        // (ADO-117634). No QL variant navigates by hash, so skip non-logout address-bar writes in QL.
        if (this.versionToggleService.isQuickLinkVersion()) {
            return;
        }
        const suffix = id ? `/${id}` : '';
        // Capture ?esignReturn right before this strip, and only here — QuickLink returns above
        // without stripping, so an earlier capture re-arms the marker after clearEsignReturn (ADO-128490).
        PathRoutingService.captureEsignReturn();
        history.replaceState(currentState, '', `#${tab}${suffix}`);
    }

    getCurrency(): Observable<string> {
        return combineLatest([this.store.pipe(select(selectContractCurrency)), this.store.pipe(select(selectCustomerCurrency))]).pipe(
            map(([contractCurrency, customerCurrency]) => contractCurrency || customerCurrency || this.getDefaultCurrency())
        );
    }

    setAuthenticated(isAuthenticated: boolean): void {
        this.store.dispatch(setAuthenticated(isAuthenticated));
    }

    getAuthenticated(): Observable<boolean> {
        return this.store.pipe(select(selectIsAuthenticated));
    }

    /**
     * Set payment section enabled to state
     * @param setPaymentSectionEnabled
     */
    setPaymentSectionEnabled(action: boolean): void {
        return this.paymentStore.dispatch(setPaymentSectionEnabled({ enabled: action }));
    }

    /**
     * Get payment section enabled or not
     * @returns Observable<boolean>
     */
    isPaymentSectionEnabled(): Observable<boolean> {
        return this.paymentStore.pipe(select(selectPaymentSectionEnabled));
    }

    setAdminLoaded(isLoaded: boolean): void {
        this.store.dispatch(setAdminLoading(isLoaded));
    }

    getAdminLoaded(): Observable<boolean> {
        return this.store.pipe(select(selectLoaded));
    }

    setContracts(contracts: ContractCP[]): void {
        this.store.dispatch(loadContractAction({ contracts, showInList: false }));
    }

    setSelectedContracts(selectedRows: string[]): void {
        this.store.dispatch(setSelectedContracts({ selectedContracts: selectedRows }));
    }

    unsetSelectedContracts(contractIds: string[]): void {
        this.store.dispatch(unsetSelectedContracts({ unselectContracts: contractIds }));
    }

    isContractAvailableInState(contractId: string): Observable<boolean> {
        return this.store.pipe(select(isContractSelected(contractId)));
    }

    getContractsTotal(): Observable<number> {
        return this.store.pipe(select(getContractsTotal));
    }

    getSelectedCreditContractsTotal(): Observable<number> {
        return this.store.pipe(select(getCreditContractsTotal));
    }

    getSelectedContractsTotal(): Observable<number> {
        return this.store.pipe(select(getSelectedContractsTotal));
    }

    getSelectedCreditContracts(): Observable<ContractCP[]> {
        return this.store.pipe(select(getSelectedCreditContracts));
    }

    getSelectedNonCreditContracts(): Observable<ContractCP[]> {
        return this.store.pipe(select(getSelectedNonCreditContracts));
    }

    loadOrganizationDepots(organizationId: string): void {
        this.store.dispatch(loadDepots({ organizationId }));
    }

    getOrganizationDepots(): Observable<Depot[]> {
        return this.store.pipe(select(selectOrganizationDepots));
    }

    loadExtCredentials(organizationId: string): void {
        this.store.dispatch(loadExternalCredentials({ organizationId }));
    }

    getOrganizationExternalCredentials(): Observable<ExternalCredentials[] | undefined> {
        return this.store.pipe(select(selectOrganizationExternalCredentials));
    }

    /**
     * Dispatch action to get payment processors
     * @param customerId
     */
    getPaymentProcessors(customerId?: string) {
        this.store.dispatch(loadPaymentProcessorAvailability({ customerId }));
    }

    /**
     * Determined Payment Processors are available to take payment or not
     * @returns Observable<boolean>
     */
    isPaymentProcessorsAvailable(): Observable<boolean | Processors[] | null> {
        return this.store.pipe(select(selectIsPaymentProcessorAvailable));
    }

    getSelectedContracts(): Observable<string[]> {
        return this.store.pipe(select(getSelectedContracts));
    }
    /**
     * determine, is maximum contract selected or not
     * @returns boolean
     */
    isMaxContractSelected(): Observable<boolean> {
        return this.store.pipe(select(isMaxContractSelected));
    }

    getSelectedContractsModels(): Observable<ContractCP[]> {
        return this.store.pipe(select(getSelectedContractsModels));
    }

    setRedirected(isRedirected: boolean) {
        this.store.dispatch(setRedirected(isRedirected));
    }

    getRedirected(): Observable<boolean> {
        return this.store.pipe(select(selectIsRedirected));
    }

    getloadVersion(orgId: string): void {
        this.store.dispatch(loadVersionRedirection({ orgId }));
    }

    setVersionRedirection(redirectionVersion: Redirection): void {
        this.store.dispatch(setVersionRedirection({ redirectionVersion }));
    }

    loadVersionRedirectionFailure(error: string): void {
        this.store.dispatch(loadVersionRedirectionFailure({ error }));
    }

    getVersionRedirection(): Observable<Redirection | null> {
        return this.store.select(selectVersionRedirection);
    }

    getStates(): Observable<ConsumerPortalState> {
        return this.store.pipe(select(selectConsumerPortalState));
    }

    getRedirection(): Observable<Redirection | null> {
        return this.store.select(selectRedirection);
    }

    getRedirectionSessionData(): Observable<LoginSession | null> {
        return this.store.select(selectRedirectionSessionData);
    }

    getIsRedirection(): Observable<boolean> {
        return this.store.select(selectIsConfigSet);
    }

    getCustomer(): Observable<Customer> {
        return this.store.pipe(select(getCustomer));
    }

    /**
     * For public (no-auth) routes: resolves orgId from URL first, then starts redirection.
     * Use this for asset tag and any future unauthenticated flows.
     */
    startPublicRedirection(toLoad: ComponentsToLoad, cpInput: ConsumerPortalConfig): void {
        this.store.dispatch(
            startPublicRedirection({
                redirection: { toLoad, orgId: '' },
                cpInput
            })
        );
    }

    startRedirection(toLoad: ComponentsToLoad, orgId: string, cpInput: ConsumerPortalConfig): void {
        this.store.dispatch(
            startRedirection({
                redirection: { toLoad, orgId: orgId || '' },
                cpInput
            })
        );
    }

    loadCustomerById(customerId: string): void {
        const customer: Customer = { id: customerId, name: '' };
        this.store.dispatch(loadCustomer({ customer }));
    }

    loadLinkAndOpen(): void {
        this.store.dispatch(loadLinkAndOpen());
    }

    loadEsignAndOpen(): void {
        this.store.dispatch(loadEsignAndOpen());
    }

    /**
     * @param tab host tab to reflect in the URL. Pass it when the caller has just dispatched setActiveTab from
     * inside an effect (processRoute$): that dispatch is queued, so the store still holds the previous tab.
     */
    openContract(selectedContractId: string, tab?: ComponentsToLoad): void {
        this.store.dispatch(openContract({ selectedContractId }));
        // Reflect the CURRENT tab (Contracts or ItemsOut), not a hardcoded one,
        // so a detail opened from My Rentals reads #ItemsOut/ContractDetail/{id}.
        let currentTab: ComponentsToLoad | undefined = tab;
        if (!currentTab) {
            this.store.pipe(select(selectActiveTab), take(1)).subscribe(t => (currentTab = t));
        }
        // Only write the ContractDetail suffix when the active tab can actually host it
        // (same allow-list the reader uses). From a non-host tab the deep link would be
        // dead on reload, so fall back to a plain #{tab}.
        const canHost = PathRoutingService.canHostContractDetail(currentTab);
        const path = selectedContractId && canHost ? `${currentTab}/${SubComponentsToLoad.ContractDetail}` : `${currentTab}`;
        this.updateLocation(path, selectedContractId && canHost ? selectedContractId : undefined);
    }

    multiPaymentContinue(): void {
        // ADO-116161: declare the subject BEFORE continueMultipayment — loadPaymentBoundaries$
        // reads it via withLatestFrom on that same action, and paymentReducer preserves it
        // across the action's full-state reset.
        this.store.dispatch(setPaymentSubject({ paymentSubject: 'contract' }));
        this.store.dispatch(continueMultipayment());
    }

    paymentClose(): void {
        this.store.dispatch(closePayment());
    }

    setPaymentOpen(open: boolean): void {
        this.store.dispatch(setPaymentOpen({ paymentOpen: open }));
    }

    isPaymentOpen(): Observable<boolean> {
        return this.store.pipe(select(getPaymentOpen));
    }

    getMultipaymentBoundaries(): Observable<PaymentMinMaxBoundary[] | null> {
        return this.store.pipe(select(selectMultipaymentBoundaries));
    }

    createTransaction(amount: string | null, transactionType: PaymentTransactionType | null): void {
        this.store.dispatch(createTransaction({ amount, transactionType }));
    }

    isContractSelectable(contractId: string): Observable<boolean | null> {
        return this.store.pipe(select(isContractSelectable(contractId)));
    }

    getTotalPaid(contractId: string): Observable<number | string> {
        return this.store.select(selectTotalPaidByContractId(contractId));
    }

    getTransactionLoaded(): Observable<boolean> {
        return this.store.pipe(select(selectTransactionLoaded));
    }

    getPaymentAppInput(): Observable<string | undefined> {
        return this.store.pipe(select(selectPaymentAppInput));
    }

    setTransactionLoaded(loaded: boolean): void {
        this.store.dispatch(setTransactionLoaded({ transactionLoaded: loaded }));
    }

    onPaymentFinished(paymentAppOutput: PayOutputEvent): void {
        this.store.dispatch(onPaymentFinished({ paymentAppOutput }));
    }

    isDialogEnabled(): Observable<boolean> {
        return this.store.pipe(select(selectDialogEnabled));
    }

    setDialogEnabled(enabled: boolean): void {
        this.store.dispatch(setDialogEnabled({ dialogEnabled: enabled }));
    }

    closePaymentDialog(): void {
        this.setDialogEnabled(false);
        this.paymentClose();
        this.setTransactionLoaded(false);
    }

    getPaymentError(): Observable<string | undefined> {
        return this.store.pipe(select(selectErrorString));
    }

    isContractSelectableAndAvailable(contractId: string): Observable<boolean> {
        const isFeatureAvailable = this.featureToggleService.isAvailable('multiplePaymentAbility');
        const isContractSelectable$ = this.isContractSelectable(contractId);

        return isContractSelectable$.pipe(map(isSelectable => isFeatureAvailable && isSelectable === true));
    }

    getLastPaymentDetails(): Observable<PaymentAppPayOutput | undefined> {
        return this.store.pipe(select(selectLastPaymentDetail));
    }

    isDialogDisplayed(): Observable<boolean> {
        return this.store.pipe(select(selectIsDialogDisplayed));
    }

    getDialogContentType(): Observable<DialogContentType | undefined> {
        return this.store.pipe(select(selectDialogContentType));
    }

    openDialog(dialogContentType: DialogContentType): void {
        this.store.dispatch(showDialog({ dialogContentType }));
    }

    toggleDialog(enabled: boolean): void {
        this.store.dispatch(setDialogDisplayed({ isDialogDisplayed: enabled }));
    }

    onSuccessVerifyPayment(detail: PaymentAppPayOutput): void {
        this.store.dispatch(successVerifyPayment({ lastPaymentDetail: detail }));
    }

    /**
     * Get Default Currency based on org Type
     * @returns string
     */
    getDefaultCurrency() {
        // Syrinx currently doesn't have currency for Customer, therefore We rather show nothing
        return this.featureToggleService.isSyrinx() ? '' : DEFAULT_CURRENCY;
    }

    /**
     * Fetch contract details
     */
    fetchContractDetail(forPayment: boolean, customerId?: string, contractId?: string): void {
        this.store.dispatch(fetchContractDetail({ forPayment, customerId, contractId }));
    }

    /**
     * get contract details from state
     */
    getContractDetail(): Observable<ContractDetail | null> {
        return this.store.pipe(select(selectContractDetail));
    }

    setContractDetail(data: ContractDetail) {
        this.store.dispatch(setContractDetailData({ data }));
    }

    /**
     * Clear contract detail on close
     */
    clearContractDetail(): void {
        this.store.dispatch(clearContractDetailInStore());
    }
    /**
     * Get error of api calls
     * @returns observable<string>
     */
    getContractDetailError(): Observable<string | null> {
        return this.store.pipe(select(selectContractError));
    }

    /**
     * Get CustomerId from config
     * @returns observable<string>
     */
    getCustomerId(): Observable<string> {
        return this.store.pipe(select(ConfigSelectors.selectCustomerId));
    }

    /**
     * Fetch Customer from Id
     * @param customerId
     */
    fetchCustomerById(customerId: string): void {
        this.store.dispatch({ type: CustomerSummaryActionTypes.LoadCustomer, payload: { id: customerId } });
    }

    processRoute(cpInput: ConsumerPortalConfig, orgId: string): void {
        this.store.dispatch(processRoute({ cpInput, orgId }));
    }

    /**
     * Get the event data from state
     * @returns Observable<PorPayEvent>
     */
    getPaymentCrossAppEvent(): Observable<PorPayEvent | null> {
        return this.store.pipe(select(selectCrossAppEvent));
    }

    loadProductDetail(
        customerId: string,
        contractId: string,
        productId: string,
        stockId: string,
        columns: string[],
        callback?: () => void,
        documentValue?: string,
        orgType?: string,
        orgId?: string
    ): void {
        this.store.dispatch(loadProduct({ customerId, contractId, productId, stockId, columns, callback, documentValue, orgType, orgId }));
    }

    /**
     * Dispatch action to load product detail via public asset tag (no auth required).
     * The orgId is resolved inside the effect via OrganizationIdService.getOrgIdFromUrl().
     * @param assetTagId - Asset tag identifier from URL query param
     */
    loadPublicProductByAssetTag(assetTagId: string): void {
        this.store.dispatch(loadPublicProductByAssetTag({ assetTagId }));
    }

    dispatchResetPageNumberContract(): void {
        this.store.dispatch(resetPageNumberContract());
    }

    dispatchResetContractsLatestToShow(): void {
        this.store.dispatch(resetContractsLatestToShow());
    }

    dispatchFetchContract(params: FetchContractsParams): void {
        const { customerId, status = [], startDate, endDate, search } = params;

        this.store.dispatch(fetchContracts({ customerId, status, startDate, endDate, search }));
    }

    /**
     * Dispatch action to convert quote to reservation
     */
    dispatchConvertQuoteToReservation(customerId: string, contractId: string): void {
        this.store.dispatch(convertQuoteToReservation({ customerId, contractId }));
    }

    getConvertedContractIds(): Observable<string[]> {
        return this.store.pipe(select(getConvertedContractIds));
    }

    getQuoteConversionInProgress(): Observable<boolean> {
        return this.store.pipe(select(getQuoteConversionInProgress));
    }

    setQuoteConversionInProgress(inProgress: boolean): void {
        this.store.dispatch(setQuoteConversionInProgress({ inProgress }));
    }

    getProductDetail(): Observable<ProductState> {
        return this.store.pipe(select(selectProduct));
    }

    getProductLoading(): Observable<boolean> {
        return this.store.pipe(select(selectProductLoading));
    }

    getProductError(): Observable<{ token: string; message?: string } | null> {
        return this.store.pipe(select(selectProductError));
    }

    resetProductDetail(): void {
        this.store.dispatch(resetProduct());
    }

    setProductDetail(data: ProductDetail) {
        this.store.dispatch(loadProductSuccess({ productDetail: [data] }));
    }

    /**
     * LineItem called off completely or not
     * @param id string
     * @returns boolean
     */
    isLineItemCalledOff(lineItemId: string): Observable<boolean> {
        return this.store.pipe(select(isLineItemCalledOff(lineItemId)));
    }

    dispatchFetchItemsOut(params: FetchItemsOutParams): void {
        const { customerId, startDate, endDate, search, status } = params;

        this.store.dispatch(fetchItemsOut({ customerId, startDate, endDate, search, status }));
    }

    getPageSizeValues(): Observable<{ selectedPageSize: number; previousPageSize: string | number }> {
        return this.store.pipe(select(selectPageSizeValues));
    }

    setPageSizeValue(value: string | number): void {
        this.store.dispatch(changePageSizeValue({ value }));
    }

    /**
     * Set App Render Device Type
     * @param isMobile
     */
    setAppDeviceType(isMobile: boolean): void {
        this.store.dispatch(setDeviceType({ isMobile }));
    }

    /**
     * Get App Render Device Type
     * @returns Observable
     */
    getAppDeviceType(): Observable<boolean> {
        return this.store.pipe(select(selectIsMobile));
    }

    /**
     * Get the transaction data
     * @returns Observable<TransactionData | undefined>
     */
    getLastPaymentTransactionData(): Observable<TransactionData | undefined> {
        return this.store.pipe(select(selectLastPaymentDetailTransactionData));
    }

    /**
     * Get the transaction Id
     * @returns Observable<string | undefined>
     */
    getLastPaymentTransactionId(): Observable<string | undefined> {
        return this.store.pipe(select(selectLastPaymentDetailTransactionId));
    }

    /**
     * Get the transaction status
     * @returns Observable<TransactionStatus | undefined>
     */
    getLastPaymentTransactionStatus(): Observable<TransactionStatus | undefined> {
        return this.store.pipe(select(selectLastPaymentDetailTransactionStatus));
    }

    /**
     * Check last payment transaction is approved
     * @returns Observable<boolean>
     */
    isPaymentApproved(): Observable<boolean> {
        return this.store.pipe(select(selectIsPaymentApproved));
    }

    /**
     * Whether the last payment was taken by POR Pay but not yet confirmed with the store
     */
    isLastPaymentConfirmationPending(): Observable<boolean> {
        return this.store.pipe(select(selectLastPaymentConfirmationPending));
    }

    /**
     * Get the transaction currencyCode
     * @returns Observable<string>
     */
    getPaymentCurrency(): Observable<string> {
        return combineLatest([this.store.pipe(select(selectTransactionCurrencyCode)), this.getCurrency()]).pipe(map(([paymentCurrency, defaultCurrency]) => paymentCurrency ?? defaultCurrency));
    }

    /**
     * Fetch invoice detail from API
     * @param customerId
     * @param invoiceId
     */
    fetchInvoiceDetail(customerId: string, invoiceId: string): void {
        this.store.dispatch(fetchInvoiceDetail({ customerId, invoiceId }));
    }

    /**
     * Get the invoice detail from store
     * @returns
     */
    getInvoiceDetail(): Observable<Invoice | null> {
        return this.store.pipe(select(selectInvoiceDetail));
    }

    /**
     * Clear invoice detail from store
     */
    clearInvoiceDetail(): void {
        this.store.dispatch(clearInvoiceDetailInStore());
    }

    setSelectedInvoices(selectedRows: string[]): void {
        this.store.dispatch(setSelectedInvoices({ selectedInvoices: selectedRows }));
    }

    unsetSelectedInvoices(invoiceIds: string[]): void {
        this.store.dispatch(unsetSelectedInvoices({ unselectInvoices: invoiceIds }));
    }

    isInvoiceAvailableInState(invoiceId: string): Observable<boolean> {
        return this.store.pipe(select(isInvoiceSelected(invoiceId)));
    }

    getInvoicesTotal(): Observable<number> {
        return this.store.pipe(select(getInvoicesTotal));
    }

    getSelectedInvoices(): Observable<string[]> {
        return this.store.pipe(select(getSelectedInvoices));
    }

    getSelectedInvoicesModels(): Observable<Invoice[]> {
        return this.store.pipe(select(getSelectedInvoicesModels));
    }

    /**
     * determine, is maximum invoice selected or not
     * @returns boolean
     */
    isMaxInvoiceSelected(): Observable<boolean> {
        return this.store.pipe(select(isMaxInvoiceSelected));
    }

    invoiceMultiPaymentContinue(): void {
        // ADO-116163: declare the subject BEFORE the continue action — loadPaymentBoundaries$
        // reads it via withLatestFrom on that same action, and paymentReducer preserves it
        // across the reset (mirrors AppFacadeService.multiPaymentContinue for contracts).
        this.store.dispatch(setPaymentSubject({ paymentSubject: 'invoice' }));
        this.store.dispatch(continueInvoiceMultipayment());
    }

    isInvoiceSelectable(invoiceId: string): Observable<boolean | null> {
        return this.store.pipe(select(isInvoiceSelectable(invoiceId)));
    }

    isInvoiceSelectableAndAvailable(invoiceId: string): Observable<boolean> {
        const isFeatureAvailable =
            this.featureToggleService.isAvailable('invoiceMultiPay') && this.featureToggleService.isAvailable('Account.SeePricing') && this.featureToggleService.isAvailable('capturePaymentFeature');

        return combineLatest([this.isInvoiceSelectable(invoiceId), this.getCustomer().pipe(startWith(null as Customer | null))]).pipe(
            map(([isSelectable, customer]) => {
                const syrinxOk = !this.featureToggleService.isSyrinx() || (customer?.Status ?? '').toLowerCase() === 'cash';
                return isFeatureAvailable && syrinxOk && isSelectable === true;
            })
        );
    }

    /**
     * Dispatch Download PDF actions
     * @param contractdetails
     * @param documentType
     */
    downloadDocument(contractdetails: GetDocumentParam, documentType: string): void {
        this.store.dispatch(downloadDocument({ contractdetails, documentType }));
    }

    /**
     * set logged user is admin
     * @param isAdmin
     */
    setAdmin(isAdmin: boolean): void {
        this.store.dispatch(setIsAdmin(isAdmin));
    }

    /**
     * get logged user is admin
     * @return Observable<boolean>
     */
    getIsAdmin(): Observable<boolean> {
        return this.store.pipe(select(selectIsAdmin));
    }

    /**
     * load customer contacts
     * @param customerId
     */
    loadCustomerContacts(customerId: string): void {
        this.store.dispatch(loadCustomerContacts({ customerId }));
    }

    getCustomerContactsLoading(): Observable<boolean> {
        return this.store.pipe(select(isCustomerContactsLoading));
    }

    /**
     * Remove contact from state
     */
    cleanCustomerContacts(): void {
        this.store.dispatch(cleanAccountContact());
    }

    /**
     * get customer contacts
     * @return Observable<ContactResponse[]>
     */
    getCustomerContacts(): Observable<ContactResponse[] | null> {
        return this.store.pipe(select(getCustomerContacts));
    }

    getDocumentDataById(docType: ProductDocumentType, contractId: string): Observable<DocumentData | undefined> {
        return this.store.pipe(select(selectDocumentDataById(docType, contractId)));
    }

    /**
     * Dispatch Download invoice PDF actions
     * @param contractdetails
     * @param invoiceId
     */
    downLoadInvoiceDocument(contractdetails: GetDocumentParam, id: string): void {
        this.store.dispatch(openOrDownLoadDocument({ id, contractdetails }));
    }

    /**
     * Assign or Revoke permission
     * @param data
     */
    assignOrRevokePermissions(data: AssignUserPermissionRequest): void {
        this.store.dispatch(assignOrRevokePermission({ data }));
    }

    /**
     * Activate or deactivate a contact, then refresh the contact list
     * @param customerId account id
     * @param contactId GAPI contact id
     * @param isActive true to activate, false to deactivate
     */
    setContactActiveStatus(customerId: string, contactId: string, isActive: boolean): void {
        this.store.dispatch(setContactActiveStatus({ customerId, contactId, isActive }));
    }

    setDefaultPaymentMid(defaultPaymentMid: string): void {
        this.paymentStore.dispatch(setDefaultPaymentMid({ defaultPaymentMid }));
    }

    getDefaultPaymentMid(): Observable<string | undefined> {
        return this.paymentStore.pipe(select(selectDefaultPaymentMid));
    }

    fetchContractDetailLineItems(customerId: string, contractId: string) {
        this.store.dispatch(fetchContractDetailLineItems({ customerId, contractId }));
    }

    fetchContractDetailPaymentHistory(customerId: string, contractId: string) {
        this.store.dispatch(fetchContractDetailPayment({ customerId, contractId }));
    }

    getContractDetailLineItems(): Observable<LineItems[] | undefined> {
        return this.store.pipe(select(selectContractLineItems));
    }

    getContractDetailPaymentHistory(): Observable<boolean> {
        return this.store.pipe(select(selectContractHasPaymentHistory));
    }

    /**
     * Fetch user assigned permission for features from DB
     */
    fetchAccountFeature(customerId: string) {
        this.store.dispatch(loadAccountFeature({ customerId }));
    }

    isManageUserDialogDisplayed(): Observable<boolean> {
        return this.store.pipe(select(selectManageUserDialogDisplayed));
    }

    showManageUserDialogDisplayed(isManageUsersDialogDisplayed: boolean): void {
        this.store.dispatch(setManageUserDialogDisplayed({ isManageUsersDialogDisplayed }));
    }

    isAccountFeaturesLoaded(): Observable<boolean> {
        return this.store.pipe(select(selectAccountFeaturesLoaded));
    }

    loadLiveLink(customerId: string): void {
        this.store.dispatch(loadLiveLink({ customerId }));
    }

    isLiveLinkLoading(): Observable<boolean> {
        return this.store.pipe(select(isLiveLinkLoading));
    }

    getLiveLinkUrl(): Observable<string | undefined> {
        return this.store.pipe(select(getLivelinkUrl));
    }

    getLivelinkExpiry(): Observable<string | undefined> {
        return this.store.pipe(select(getLivelinkExpiry));
    }

    getContracts(): Observable<ContractCP[]> {
        return this.store.pipe(select(getContracts));
    }

    getTrackedItems(): Observable<TrackingResponse[] | undefined> {
        return this.store.pipe(select(getTrackedItems));
    }

    setTrackedItems(trackedItems: TrackingResponse[]): void {
        this.store.dispatch(setTrackedItems({ trackedItems }));
    }

    /**
     * Payment Token Methods
     *
     * These methods support payment token initialization flow for external embedding.
     * Used when external sites provide a paymentToken in config.
     */

    /**
     * Set customer ID in config state
     * Used during payment token initialization
     * @param customerId - Customer ID from token payload
     */
    setCustomerId(customerId: string): void {
        this.store.dispatch(PaymentTokenActions.setCustomerId({ customerId }));
    }

    /**
     * Set route parameters in config state
     * Used during payment token initialization
     * @param routeParams - Route parameters (contractIds, contractCustomerId, amount)
     */
    setRouteParams(routeParams: { contractIds: string[]; contractCustomerId: string; amount?: number }): void {
        this.store.dispatch(PaymentTokenActions.setRouteParams(routeParams));
    }

    /**
     * Set authentication data in config state
     * Used during payment token initialization
     * @param auth - Auth data (AccessToken, RefreshToken, Email)
     */
    // eslint-disable-next-line @typescript-eslint/naming-convention
    setAuth(auth: { AccessToken: string; RefreshToken: string | null; Email: string }): void {
        this.store.dispatch(PaymentTokenActions.setAuth(auth));
    }

    /**
     * Set route-to navigation data in config state
     * Used during payment token initialization
     * @param routeTo - Route-to data (route, routeParams)
     */
    setRouteTo(routeTo: { route: string; routeParams: { contractIds: string[]; contractCustomerId: string; amount?: number } }): void {
        this.store.dispatch(PaymentTokenActions.setRouteTo(routeTo));
    }
}
